import React, { useMemo } from "react";
import ViewRosterDay from "./ViewRosterDay";
import type * as type from "@vTypes";
import type {
  IssuedSnapshotAssignmentEntry,
  Roster,
} from "@vTypes/snapshot-type.ts";
import type { MyIssuedRosterTransfer } from "@/hooks/useMyIssuedRoster";
import { normalizeScheduleCellCode } from "@utils/shift-util.ts";
import { getSnapshotCellDisplayColor } from "@/utils/snapshotRosterDisplay";
import { cn } from "@utils/cn.ts";

type AssignmentBar = { label: string; color: string; spanDays: number };
type WardSlice = { key: string; label: string; color: string };

/** 특정 일자의 소속 병동(파견 대상 또는 기본 병동) 라벨/색상 */
function wardSliceForDay(
  transfers: MyIssuedRosterTransfer[] | undefined,
  day: number,
  daysInMonth: number,
  wardColorByGroupId: Map<string, string> | undefined,
  currentWardName?: string,
  currentWardGroupId?: string
): WardSlice | undefined {
  for (const t of transfers ?? []) {
    const s = Math.max(1, Math.floor(t.period_start_day));
    const e = Math.min(daysInMonth, Math.floor(t.period_end_day));
    if (s > e || day < s || day > e) continue;

    const groupId = (t.target_group_id ?? "").trim();
    const label =
      (t.target_group_name ?? "").trim() ||
      (t.reason ?? "").trim() ||
      "기간배정";
    const color = wardColorByGroupId?.get(groupId) ?? "#64748b";
    return { key: `transfer:${groupId || label}`, label, color };
  }

  const baseLabel = (currentWardName ?? "").trim();
  if (baseLabel === "") return undefined;
  const baseGroupId = (currentWardGroupId ?? "").trim();
  const baseColor = wardColorByGroupId?.get(baseGroupId) ?? "#64748b";
  return {
    key: `home:${baseGroupId || baseLabel}`,
    label: baseLabel,
    color: baseColor,
  };
}

/** 월~일 행 경계 + 병동 변경 지점 기준으로 시작 셀에서만 span 바 생성 */
function transferBarForCalendarDay(
  transfers: MyIssuedRosterTransfer[] | undefined,
  day: number,
  daysInMonth: number,
  startDay: number,
  wardColorByGroupId: Map<string, string> | undefined,
  currentWardName?: string,
  currentWardGroupId?: string
): AssignmentBar | undefined {
  const colIndex = (startDay + (day - 1)) % 7;
  const today = wardSliceForDay(
    transfers,
    day,
    daysInMonth,
    wardColorByGroupId,
    currentWardName,
    currentWardGroupId
  );
  if (!today) return undefined;

  const prev =
    day > 1
      ? wardSliceForDay(
          transfers,
          day - 1,
          daysInMonth,
          wardColorByGroupId,
          currentWardName,
          currentWardGroupId
        )
      : undefined;
  const chunkStart = day === 1 || colIndex === 0 || prev?.key !== today.key;
  if (!chunkStart) return undefined;

  const weekEndDay = Math.min(daysInMonth, day + (7 - colIndex) - 1);
  let spanEnd = day;
  for (let d = day + 1; d <= weekEndDay; d++) {
    const next = wardSliceForDay(
      transfers,
      d,
      daysInMonth,
      wardColorByGroupId,
      currentWardName,
      currentWardGroupId
    );
    if (!next || next.key !== today.key) break;
    spanEnd = d;
  }

  return {
    label: today.label,
    color: today.color,
    spanDays: spanEnd - day + 1,
  };
}

function dateStrInAssignmentRange(
  dateStr: string,
  e: IssuedSnapshotAssignmentEntry
): boolean {
  const start = (e.start_date ?? "").slice(0, 10);
  if (start === "") return false;
  const endRaw = e.end_date;
  const end = (endRaw == null || endRaw === "" ? e.start_date : endRaw).slice(
    0,
    10
  );
  return dateStr >= start && dateStr <= end;
}

function firstAssignmentForDate(
  entries: IssuedSnapshotAssignmentEntry[] | undefined,
  dateStr: string
): IssuedSnapshotAssignmentEntry | undefined {
  if (!entries?.length) return undefined;
  for (const e of entries) {
    if (dateStrInAssignmentRange(dateStr, e)) return e;
  }
  return undefined;
}

function ViewRosterCalendar({
  currentScheduleDate,
  holidayDates,
  loginUser,
  rosterSheet,
  myAssignments,
  myTransfers,
  wardColorByGroupId,
  currentWardName,
  currentWardGroupId,
}: {
  currentScheduleDate: { year: number; month: number };
  holidayDates: string[];
  loginUser: type.LoginUser | null;
  rosterSheet: Roster;
  myAssignments?: IssuedSnapshotAssignmentEntry[];
  /** GET /me `transfers`(또는 부모에서 단일 transfer 병합) */
  myTransfers?: MyIssuedRosterTransfer[];
  wardColorByGroupId?: Map<string, string>;
  /** 현재 로그인 사용자 소속 병동명(예: 1병동) */
  currentWardName?: string;
  /** 현재 로그인 사용자 소속 병동 ID (팔레트 색상 조회용) */
  currentWardGroupId?: string;
}) {
  /**
   *
   * Map으로 유저정보 매핑 해서 자기 스케줄표 뿌리기
   * */

  const nurseMap = useMemo(() => {
    return new Map(rosterSheet.nurses.map((n) => [String(n.nurse_id), n]));
  }, [rosterSheet.nurses]);

  const myNurseData = nurseMap.get(String(loginUser?.nurse_id));

  if (!myNurseData) {
    return (
      <div className="w_myWanted__calendar w_myWanted__selectCalendar">
        <div className="empty-message">내 근무표 정보를 찾을 수 없습니다.</div>
      </div>
    );
  }

  const mySchedule = myNurseData.schedule;
  const shiftColors = rosterSheet.shift_colors ?? {};

  const getShiftColor = (raw: unknown) =>
    getSnapshotCellDisplayColor(raw, shiftColors);

  const normalizeShiftCode = (shiftCode: string | null | undefined) => {
    const raw = (shiftCode ?? "").trim();
    if (!raw) return "";
    if (raw.length > 1 && raw.split("").every((ch) => ch === raw[0])) {
      return raw[0];
    }
    return raw;
  };

  const coworkerList = mySchedule.map((c, i) => {
    const mainShiftCode = normalizeShiftCode(normalizeScheduleCellCode(c));
    if (["D", "E", "N"].includes(mainShiftCode)) {
      return rosterSheet.nurses.filter(
        (n) =>
          String(n.nurse_id) !== String(loginUser?.nurse_id) &&
          normalizeScheduleCellCode(n.schedule[i]) ===
            normalizeScheduleCellCode(mySchedule[i])
      );
    } else {
      return []; // DEN이 아닌 경우 빈 배열
    }
  });

  const firstDay = new Date(
    currentScheduleDate.year,
    currentScheduleDate.month - 1,
    1
  );

  const lastDay = new Date(
    currentScheduleDate.year,
    currentScheduleDate.month,
    0
  );

  const daysInMonth = lastDay.getDate();
  let startDay = firstDay.getDay(); // 한 주 시작 : 일
  startDay = (startDay + 6) % 7; // 한 주 시작 : 월

  const nextMonthDay = new Date(
    currentScheduleDate.year,
    currentScheduleDate.month,
    1
  ).getDay();

  const weeks: React.JSX.Element[][] = [];
  let currentWeek: React.JSX.Element[] = [];

  for (let i = 0; i < startDay; i++) {
    const prevDate = new Date(firstDay);
    prevDate.setDate(prevDate.getDate() - (startDay - i));
    const day = prevDate.getDate();
    const weekend = prevDate.getDay() % 7;

    currentWeek.push(
      <td
        className={`opacity-30 ${weekend === 6 ? "calendar_sat" : weekend === 0 ? "calendar_sun" : ""}`}
        key={`prev-${i}`}
      >
        <div className="calendar-date">{day}</div>
      </td>
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${currentScheduleDate.year}-${String(currentScheduleDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const dayIndex = day - 1;
    const myShiftRaw = mySchedule[dayIndex];
    const myShift = normalizeScheduleCellCode(myShiftRaw) || null;
    const weekend = (day + startDay) % 7;
    const isHol = holidayDates.includes(dateStr);

    const transferBar = transferBarForCalendarDay(
      myTransfers,
      day,
      daysInMonth,
      startDay,
      wardColorByGroupId,
      currentWardName,
      currentWardGroupId
    );

    const hit = firstAssignmentForDate(myAssignments, dateStr);
    const rawG = (hit?.target_group_id ?? "").trim();
    const legacyLabel = (hit?.target_group_name ?? "").trim();
    const legacyColor =
      hit != null ? (wardColorByGroupId?.get(rawG) ?? "#64748b") : undefined;

    const hasTransferList = (myTransfers?.length ?? 0) > 0;
    const assignmentBar: AssignmentBar | null =
      transferBar ??
      (!hasTransferList && legacyLabel !== "" && legacyColor != null
        ? { label: legacyLabel, color: legacyColor, spanDays: 1 }
        : null);

    currentWeek.push(
      <td
        key={dateStr}
        className={cn(
          assignmentBar != null &&
            assignmentBar.spanDays > 1 &&
            "!overflow-visible"
        )}
      >
        <ViewRosterDay
          day={day}
          holiday={{ weekend: weekend, isHol: isHol }}
          myShift={myShift}
          displayShiftId={myShift}
          color={getShiftColor(myShiftRaw)}
          coworkerList={coworkerList[dayIndex]}
          assignmentBar={assignmentBar}
        />
      </td>
    );

    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  const remainingCells = 7 - currentWeek.length;

  if (remainingCells > 0) {
    {
      Array.from({ length: remainingCells }, (_, i) => {
        const weekend = (nextMonthDay + i) % 7;
        currentWeek.push(
          <td
            key={`empty-${i + 1}`}
            className={`opacity-30 ${weekend === 6 ? "calendar_sat" : weekend === 0 ? "calendar_sun" : ""}`}
          >
            <div className="calendar-date">{i + 1}</div>
          </td>
        );
      });
    }
    weeks.push(currentWeek);
  }

  return (
    <table className="w_myWanted__calendar w_myWanted__selectCalendar">
      <thead>
        <tr>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            월
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            화
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            수
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            목
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sub !text-[15px]"
            )}
          >
            금
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sat !text-[15px]"
            )}
          >
            토
          </th>
          <th
            className={cn(
              "font-pretendard font-semibold text-text-sun !text-[15px]"
            )}
          >
            일
          </th>
        </tr>
      </thead>
      <tbody className="calendar-body">
        {weeks.map((week, idx) => (
          <tr key={idx}>{week}</tr>
        ))}
      </tbody>
    </table>
  );
}

export default ViewRosterCalendar;
